import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import {
  FileText,
  Plus,
  Search,
  Filter,
  Eye,
  Trash2,
  Building2, Pencil
} from "lucide-react";

const API = "https://nissi-india.com/api";

const PurchaseOrderListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const loginuserid = Number(userId) || 0;
  const fld_branchid = Number(branchId) || 0;

  const normalizedBranch = String(branch || "").trim().toUpperCase();
  const isHO =
    normalizedBranch === "HO" ||
    normalizedBranch === "HEAD-OFFICE" ||
    normalizedBranch === "HEAD OFFICE";

  const [location, setLocation] = useState(isHO ? "HO" : "BRANCH");
  const [selectedBranch, setSelectedBranch] = useState(
    isHO ? "" : String(fld_branchid)
  );

  const [branchType, setBranchType] = useState(isHO ? "HO" : "Branch");
  const [branchList, setBranchList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [purchaseOrderNo, setPurchaseOrderNo] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [status, setStatus] = useState("");
  const [store, setStore] = useState("");
  const [supplier, setSupplier] = useState("");

  const [showFilters, setShowFilters] = useState(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [yyyy, mm, dd] = dateStr.split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${dd}/${mm}/${yyyy}`;
  };

  useEffect(() => {
    if (!branchId) return;

    axios
      .get(
        `${API}/po_fillDropdowns?branchid=${branchId}&branchtype=${
          location === "HO" ? "HO" : "BRANCH"
        }`,
        { headers: commonHeaders }
      )
      .then((res) => {
        const stores = res.data?.data?.stores || [];
        const suppliers = res.data?.data?.suppliers || [];
        const defaultStoreId = res.data?.data?.defaultStoreId || "";

        setBranchList(stores);
        setSupplierList(suppliers);

        if (!store && defaultStoreId) {
          setStore(String(defaultStoreId));
          if (!isHO) {
            setSelectedBranch(String(branchId));
          }
        }
      })
      .catch((err) => {
        toast.error("Failed to load dropdowns");
        console.error(err);
        setBranchList([]);
        setSupplierList([]);
      });
  }, [branchId, location, commonHeaders]);

  const handleSearch = () => {
    const payload = {
      branchid: selectedBranch || fld_branchid || 0,
      branchtype: location === "HO" ? "HO" : "BRANCH",
      purreqtype: location === "HO" ? "H" : "B",
      pono: purchaseOrderNo || "",
      fromdate: fromDate ? formatDate(fromDate) : "",
      todate: toDate ? formatDate(toDate) : "",
      supplierid: supplier || "",
      projectcodeid: projectCode || "",
      status: status || "",
      store: store || "",
      loginuserid,
    };

    setLoading(true);
    axios
      .post(`${API}/po_fillGridFull`, payload, {
        headers: commonHeaders,
      })
      .then((res) => {
        const responseData = res.data;

        if (responseData && Array.isArray(responseData.purchaseOrders)) {
          setData(responseData.purchaseOrders);
          toast.success("Data loaded successfully");
        } else {
          console.warn("API did not return purchaseOrders array:", responseData);
          setData([]);
        }
      })
      .catch((err) => {
        toast.error("Failed to fetch purchase orders");
        console.error(err);
        setData([]);
      })
      .finally(() => setLoading(false));
  };

  const handleBranchTypeChange = (value) => {
    setBranchType(value);

    if (value === "HO") {
      setLocation("HO");
      setSelectedBranch("");
    } else {
      setLocation("BRANCH");
      setSelectedBranch(String(fld_branchid || ""));
    }
  };

  const clearFilters = () => {
    setPurchaseOrderNo("");
    setProjectCode("");
    setStatus("");
    setSupplier("");
    setFromDate("");
    setToDate("");
    setStore("");
    setSelectedBranch(isHO ? "" : String(fld_branchid || ""));
  };

  const getPoId = (item) =>
  item.fld_purchaseorderid ||
  item.fld_PurchaseOrderid ||
  item.purchaseorderid ||
  item.poid ||
  item.id ||
  null;

const handleView = (item) => {
  const id = getPoId(item);
  if (!id) return toast.error("PO id missing");
  navigate("/inventory/PurchaseOrder_EntryGST", {
    state: { purchaseOrderId: id, mode: "view" },
  });
};

const handleEdit = (item) => {
  const id = getPoId(item);
  if (!id) return toast.error("PO id missing");
  navigate("/inventory/PurchaseOrder_EntryGST", {
    state: { purchaseOrderId: id, mode: "edit" },
  });
};

const handleDelete = async (item) => {
  const id = getPoId(item);
  if (!id) {
    toast.error("PO id missing");
    return;
  }
  if (!window.confirm(`Delete PO ${item.fld_branchwiseno || item.pono || id}?`)) {
    return;
  }
  try {
    const res = await axios.delete(`${API}/deletePurchaseOrder`, {
      data: { ids: [id] },
      headers: commonHeaders,
    });
    if (res.data?.success) {
      toast.success(res.data.message || "Deleted");
      handleSearch();
    } else {
      toast.error(res.data?.message || "Delete failed");
    }
  } catch (err) {
    toast.error(err.response?.data?.message || "Delete failed");
  }
};

  const getStatusBadge = (poStatus) => {
    if (poStatus === "Closed") return "bg-green-100 text-green-800";
    if (poStatus === "Waiting") return "bg-yellow-100 text-yellow-800";
    if (poStatus === "Canceled" || poStatus === "Cancel")
      return "bg-red-100 text-red-800";
    if (poStatus === "GRN Exist") return "bg-blue-100 text-blue-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Purchase Order Listing</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Purchase Order Records</h1>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Search and manage purchase orders
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {data.length} records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${showFilters ? (isDark ? "bg-slate-800" : "bg-slate-100") : ""}`}
              >
                <Filter size={16} />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate("/inventory/PurchaseOrder_Add")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
              >
                <Plus size={16} />
                Add New
              </button>
            </div>
          </div>

          {/* Branch Type Toggle */}
          <div>
            <label className={labelClass}>Branch Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => handleBranchTypeChange("HO")}
                disabled={!isHO}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "HO"
                    ? `${buttonClass} ${hoverClass} text-white shadow`
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${!isHO ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Building2 size={16} />
                Head Office
              </button>

              <button
                onClick={() => handleBranchTypeChange("Branch")}
                disabled={isHO}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "Branch"
                    ? "bg-blue-600 text-white shadow"
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${isHO ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Branch
              </button>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        {showFilters && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-lg font-bold">Search Filters</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Purchase Order No</label>
                  <input
                    type="text"
                    value={purchaseOrderNo}
                    onChange={(e) => setPurchaseOrderNo(e.target.value)}
                    placeholder="Enter Purchase Order Number"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Branch / Store</label>
                  <select
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Branch / Store</option>
                    {branchList.map((item, index) => (
                      <option
                        key={index}
                        value={item.fld_storeid || item.fld_branchid}
                      >
                        {item.fld_storename || item.fld_branchname || "Unnamed"}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Supplier</label>
                  <select
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All Suppliers</option>
                    {supplierList.map((item, index) => (
                      <option key={index} value={item.fld_supplierid}>
                        {item.fld_suppliername}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Project Code</label>
                  <input
                    type="text"
                    value={projectCode}
                    onChange={(e) => setProjectCode(e.target.value)}
                    placeholder="Enter Project Code"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All Status</option>
                    <option value="Waiting">Waiting</option>
                    <option value="Closed">Closed</option>
                    <option value="Canceled">Canceled</option>
                    <option value="GRN Exist">GRN Exist</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={store}
                    onChange={(e) => setStore(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All Stores</option>
                    {branchList.map((item, index) => (
                      <option key={index} value={item.fld_storeid}>
                        {item.fld_storename || "Unnamed Store"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={clearFilters}
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  Clear Filters
                </button>

                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                >
                  <Search size={16} />
                  {loading ? "Searching..." : "Search Records"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <h3 className="text-xl font-bold">Purchase Order List</h3>
            </div>
          </div>

          {data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      PO No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Supplier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Project
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Store
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark ? "divide-gray-600" : "divide-gray-200"
                  }`}
                >
                  {data.map((item, index) => {
                    const poNo =
                      item.pono ||
                      item.purchaseorder_no ||
                      item.fld_purchaseorder_no ||
                      item.fld_branchwiseno ||
                      "-";

                    const poDate =
                      item.podate ||
                      item.date ||
                      item.fld_date ||
                      item.fld_purchaseorderdate ||
                      "-";

                    const supplierName =
                      item.suppliername || item.fld_suppliername || "-";

                    const projectName =
                      item.projectname ||
                      item.fld_projectname ||
                      item.projectcode ||
                      item.fld_projectcode ||
                      "-";

                    const storeName =
                      item.storename ||
                      item.fld_storename ||
                      item.store ||
                      "-";

                    const totalAmount =
                      item.totalamount ||
                      item.fld_total ||
                      item.totalvalue ||
                      item.fld_totalvalue ||
                      0;

                    const poStatus =
                      item.status || item.Status || item.fld_status || "-";

                    return (
                      <tr
                        key={item.id || item.fld_purchaseorderid || index}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium">{index + 1}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {poNo}
                          </span>
                        </td>
                        <td className="px-4 py-3">{poDate}</td>
                        <td className="px-4 py-3 font-medium">{supplierName}</td>
                        <td className="px-4 py-3">{projectName}</td>
                        <td className="px-4 py-3">{storeName}</td>
                        <td className="px-4 py-3 font-medium">
                          ₹{Number(totalAmount || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
                              poStatus
                            )}`}
                          >
                            {poStatus}
                          </span>
                        </td>
                      <td className="px-4 py-3 text-center">
  <div className="flex items-center justify-center gap-2">
    <button
      type="button"
      onClick={() => handleView(item)}
      className="p-1.5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition"
      title="View"
    >
      <Eye size={16} />
    </button>
    <button
      type="button"
      onClick={() => handleEdit(item)}
      className="p-1.5 rounded-full bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition"
      title="Edit"
    >
      <Pencil size={16} />
    </button>
    <button
      type="button"
      onClick={() => handleDelete(item)}
      className="p-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition"
      title="Delete"
    >
      <Trash2 size={16} />
    </button>
  </div>
</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Records Found
              </h3>
              <p className="text-gray-500 text-center max-w-md text-sm">
                Try adjusting your search filters or add a new purchase order
                record.
              </p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseOrderListing;